import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchCLTVCustomerAnalytics,
  fetchCLTVCustomerAnalyticsSegments,
  fetchCLTVKpis,
  fetchCLTVSegmentEvolution,
  fetchCLTVSpendingPatterns,
  type CLTVKPIResponse,
  type CustomerAnalyticsResponse,
  type SegmentEvolutionResponse,
  type SpendingPatternsResponse,
} from "src/services/CLTVDashboardAPI";
import type { ApiLang } from "src/utils/apiLang";
import i18n from "src/i18n/config";


/** camelCase version consumed by Recharts LineChart */
export interface CltvEvolutionPoint {
  label: string;
  loyal: number;
  highSpender: number;
  likelyToChurn: number;
  oneTimer: number;
  idle: number;
  cartAbandoners: number;
  indecisive: number;
  unsubscribed: number;
}

export interface UseCLTVDashboardParams {
  startDate: string;
  endDate: string;
  lang: ApiLang;
  page?: number;
  pageSize?: number;
  search?: string;
  segment?: string[];
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export function useCLTVDashboard({
  startDate,
  endDate,
  lang,
  page = 1,
  pageSize = 10,
  search = "",
  segment,
  sortBy = "",
  sortDir,
}: UseCLTVDashboardParams) {
  const [kpiBundle, setKpiBundle] = useState<CLTVKPIResponse | null>(null);
  const [spendingPatterns, setSpendingPatterns] = useState<SpendingPatternsResponse["data"]>([]);
  const [segmentEvolution, setSegmentEvolution] = useState<CltvEvolutionPoint[]>([]);
  const [customerAnalytics, setCustomerAnalytics] = useState<CustomerAnalyticsResponse | null>(null);
  const [customerSegments, setCustomerSegments] = useState<string[]>([]);

  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [chartsError, setChartsError] = useState<string | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState<string | null>(null);

  const chartsKeyRef = useRef("");
  const customerKeyRef = useRef("");

  const fetchChartsAndKpis = useCallback(async () => {
    if (!endDate) {
      return;
    }

    const primaryDate = endDate;
    const secondaryDate = startDate;
    const key = `${primaryDate}|${secondaryDate}|${lang}`;
    if (chartsKeyRef.current === key) {
      return;
    }
    chartsKeyRef.current = key;

    setKpiLoading(true);
    setChartsLoading(true);
    setKpiError(null);
    setChartsError(null);

    const toMessage = (reason: unknown) =>
      reason instanceof Error
        ? reason.message
        : i18n.t("cltv.errors.requestFailed");

    try {
      const [kpiSettled, spendSettled, evolutionSettled] = await Promise.allSettled([
        fetchCLTVKpis(primaryDate, secondaryDate, lang),
        fetchCLTVSpendingPatterns(lang),
        fetchCLTVSegmentEvolution(primaryDate, lang),
      ]);

      if (kpiSettled.status === "fulfilled") {
        setKpiBundle(kpiSettled.value);
        setKpiError(null);
      } else {
        setKpiBundle(null);
        setKpiError(toMessage(kpiSettled.reason));
      }

      if (spendSettled.status === "fulfilled") {
        setSpendingPatterns(spendSettled.value.data ?? []);
      } else {
        setSpendingPatterns([]);
      }

      if (evolutionSettled.status === "fulfilled") {
        setSegmentEvolution(
          (evolutionSettled.value.data ?? []).map(
            (row: SegmentEvolutionResponse["data"][number]) => ({
              label: row.date,
              loyal: row.loyal ?? row.loyal_customers ?? 0,
              highSpender: row.highSpender ?? row.high_spenders ?? 0,
              likelyToChurn: row.likelyToChurn ?? row.likely_to_churn ?? 0,
              oneTimer: row.oneTimer ?? row.one_timers ?? 0,
              idle: row.idle ?? row.idle_customers ?? 0,
              cartAbandoners: row.cartAbandoner ?? row.cartAbandoners ?? row.cart_abandoners ?? 0,
              indecisive: row.indecisive ?? row.indecisive_buyers ?? 0,
              unsubscribed: row.unsubscribed ?? 0,
            })
          )
        );
      } else {
        setSegmentEvolution([]);
      }

      const chartFailures: string[] = [];
      if (spendSettled.status === "rejected") {
        chartFailures.push(toMessage(spendSettled.reason));
      }
      if (evolutionSettled.status === "rejected") {
        chartFailures.push(toMessage(evolutionSettled.reason));
      }
      setChartsError(chartFailures.length ? chartFailures.join(" • ") : null);
    } catch (err) {
      setKpiError(toMessage(err));
      setChartsError(toMessage(err));
      setKpiBundle(null);
      setSpendingPatterns([]);
      setSegmentEvolution([]);
      chartsKeyRef.current = "";
    } finally {
      setKpiLoading(false);
      setChartsLoading(false);
    }
  }, [startDate, endDate, lang]);

  const fetchCustomers = useCallback(async () => {
    if (!endDate) {
      return;
    }

    const primaryDate = endDate;
    const key = `${primaryDate}|${lang}|${page}|${pageSize}|${search}|${segment?.join(",") ?? ""}|${sortBy}|${sortDir ?? ""}`;
    if (customerKeyRef.current === key) {
      return;
    }
    customerKeyRef.current = key;

    setCustomerLoading(true);
    setCustomerError(null);

    try {
      const res = await fetchCLTVCustomerAnalytics({
        primary_date: primaryDate,
        lang,
        page,
        page_size: pageSize,
        search: search.trim() || undefined,
        segment: segment?.length ? segment : undefined,
        sort_by: sortBy.trim() || undefined,
        sort_order: sortDir,
      });
      setCustomerAnalytics(res);
    } catch (err) {
      setCustomerError(
        err instanceof Error ? err.message : i18n.t("cltv.errors.loadCustomerFailed"),
      );
      setCustomerAnalytics(null);
      customerKeyRef.current = "";
    } finally {
      setCustomerLoading(false);
    }
  }, [endDate, lang, page, pageSize, search, segment, sortBy, sortDir]);

  useEffect(() => {
    if (!endDate) {
      return;
    }

    let isActive = true;
    fetchCLTVCustomerAnalyticsSegments(endDate, lang)
      .then(res => {
        if (isActive) {
          setCustomerSegments(res.segments);
        }
      })
      .catch(() => {
        if (isActive) {
          setCustomerSegments([]);
        }
      });

    return () => {
      isActive = false;
    };
  }, [endDate, lang]);

  useEffect(() => {
    fetchChartsAndKpis();
  }, [fetchChartsAndKpis]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const refetchAll = useCallback(async () => {
    chartsKeyRef.current = "";
    customerKeyRef.current = "";
    await Promise.all([fetchChartsAndKpis(), fetchCustomers()]);
  }, [fetchChartsAndKpis, fetchCustomers]);

  return {
    kpiCards: kpiBundle?.kpiCards ?? [],
    segmentDistribution: kpiBundle?.segmentDistribution ?? [],
    predictiveInsights: kpiBundle?.predictiveInsights ?? [],
    spendingPatterns,
    segmentEvolution,
    customerAnalytics,
    customerSegments,
    kpiLoading,
    kpiError,
    chartsLoading,
    chartsError,
    customerLoading,
    customerError,
    refetch: refetchAll,
  };
}
